import React, { ReactElement, useEffect, useState } from 'react';
import {
    Button,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Space,
    Switch,
    message,
} from 'antd';
import { useAppDispatch } from 'store';
import {
    archiveAdminUser,
    patchAdminUser,
    reissueAdminTicket,
} from 'store/slices/adminUsersSlice';
import * as adminApi from 'js/utils/adminApi';
import { IAdminUser } from 'store/slices/types';

interface Props {
    user: IAdminUser | null;
    onClose: () => void;
}

export default function UserDrawer({ user, onClose }: Props): ReactElement {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const [pwdOpen, setPwdOpen] = useState(false);
    const [pwd, setPwd] = useState('');

    useEffect(() => {
        if (user) form.setFieldsValue(user);
    }, [user, form]);

    if (!user) return <Drawer open={false} onClose={onClose} />;

    const onSave = async (): Promise<void> => {
        const values = await form.validateFields();
        await dispatch(
            patchAdminUser({ id: user.id, payload: values })
        ).unwrap();
        message.success('Сохранено');
    };

    const onArchive = async (): Promise<void> => {
        await dispatch(archiveAdminUser(user.id)).unwrap();
        message.success('Аннулирован');
        onClose();
    };

    const onReissue = async (): Promise<void> => {
        await dispatch(reissueAdminTicket(user.id)).unwrap();
        message.success('Билет будет перевыпущен при следующем обращении');
    };

    const onResetPassword = async (): Promise<void> => {
        await adminApi.resetPassword(user.id, pwd);
        setPwdOpen(false);
        setPwd('');
        message.success('Пароль сброшен');
    };

    return (
        <Drawer
            open={!!user}
            onClose={onClose}
            title={`Участник #${user.id}`}
            width={420}
            extra={
                <Space>
                    <Button onClick={onReissue}>Перевыпустить билет</Button>
                    <Popconfirm
                        title="Аннулировать участника?"
                        onConfirm={onArchive}
                    >
                        <Button danger>Аннулировать</Button>
                    </Popconfirm>
                </Space>
            }
        >
            <Form form={form} layout="vertical" initialValues={user}>
                <Form.Item name="first_name" label="Имя">
                    <Input />
                </Form.Item>
                <Form.Item name="last_name" label="Фамилия">
                    <Input />
                </Form.Item>
                <Form.Item name="middle_name" label="Отчество">
                    <Input />
                </Form.Item>
                <Form.Item name="username" label="Логин">
                    <Input />
                </Form.Item>
                <Form.Item name="event_id" label="Event ID">
                    <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="is_staff"
                    label="Staff"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name="is_superuser"
                    label="Superuser"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Space>
                    <Button type="primary" onClick={onSave}>
                        Сохранить
                    </Button>
                    <Button onClick={(): void => setPwdOpen(true)}>
                        Сбросить пароль
                    </Button>
                </Space>
            </Form>
            <Modal
                open={pwdOpen}
                title="Новый пароль"
                onCancel={(): void => setPwdOpen(false)}
                onOk={onResetPassword}
                okButtonProps={{ disabled: pwd.length < 4 }}
            >
                <Input.Password
                    value={pwd}
                    onChange={(e): void => setPwd(e.target.value)}
                    autoFocus
                />
            </Modal>
        </Drawer>
    );
}
